import React, { useState } from 'react';
import { Card, Row, Col, Progress } from 'antd';
import { 
  EnvironmentOutlined, 
  TeamOutlined, 
  CustomerServiceOutlined,
  ShopOutlined,
  CarOutlined,
  RocketOutlined,
  EyeOutlined,
  PercentageOutlined,
  MessageOutlined,
  MailOutlined,
  DollarOutlined,
  LineChartOutlined
} from '@ant-design/icons';
import './Dashboard.css';

function Dashboard() {
  const [stats] = useState({
    locations: 9,
    vehicles: 47,
    campaigns: 42,
    routeMiles: 22.7,
    impressionsPerMile: 87.1,
    ctr: 3.82
  });

  const [planDetails] = useState({
    routeIQ: {
      name: 'RouteIQ',
      subtitle: 'Vehicle & Location Tracking',
      vehicles: { count: 47, tier: 'Medium Tier' },
      locations: { count: 9, tier: 'Enterprise Tier' },
      pricing: [
        { label: 'Vehicles (47 × $39)', value: '$1,833' },
        { label: 'Locations (9 × $99)', value: '$891' }
      ],
      subtotal: '$2,724/mo'
    },
    proximityIQ: {
      name: 'ProximityIQ',
      subtitle: '3x Scale Packages',
      smsCredits: { used: 7847, total: 27000 },
      emailCredits: { used: 6250, total: 27000 },
      basePlatform: { label: 'Base Platform Fee (9 locations × $150)', value: '$1350/mo' },
      scalePackages: { label: '9x Scale Packages ($89/location)', value: '$801/mo' },
      monthlyTotal: '$2151/mo'
    },
    billingTotal: '$4,875'
  });

  const [analytics] = useState({
    sms: [
      { label: 'Active Campaigns', value: 13, change: 12.5, icon: <MessageOutlined /> },
      { label: 'Number Delivered', value: '37.3K', change: 2.1, icon: <MessageOutlined /> },
      { label: 'CTR', value: '4.04%', change: null, icon: <PercentageOutlined /> },
      { label: 'Total Miles', value: '22.7K', change: null, icon: <CarOutlined /> },
      { label: 'Impressions / Mile', value: '1.6', change: 8.2, icon: <EyeOutlined /> }
    ],
    email: [
      { label: 'Active Campaigns', value: 10, change: 18.2, icon: <MailOutlined /> },
      { label: 'Emails Delivered', value: '53.1K', change: 3.2, icon: <MailOutlined /> },
      { label: 'Open Rate', value: '24.15%', change: 1.8, icon: <PercentageOutlined /> },
      { label: 'CTR', value: '5.526%', change: null, icon: <PercentageOutlined /> },
      { label: 'Total Miles', value: '22.7K', change: 8.7, icon: <CarOutlined /> },
      { label: 'Impressions / Mile', value: '2.3', change: null, icon: <EyeOutlined /> }
    ],
    meta: [
      { label: 'Number of Campaigns', value: 11, change: 22.4, icon: <RocketOutlined /> },
      { label: 'Total Spent', value: '$12,589', change: 6.4, icon: <DollarOutlined /> },
      { label: 'CTR', value: '2.283%', change: null, icon: <PercentageOutlined /> },
      { label: 'CPC', value: '$0.61', change: -8.2, icon: <DollarOutlined /> },
      { label: 'Impressions', value: '955.0K', change: 15.3, icon: <EyeOutlined /> },
      { label: 'Impressions / Mile', value: '42', change: 5.1, icon: <LineChartOutlined /> }
    ],
    google: [
      { label: 'Active Campaigns', value: 8, change: 18.2, icon: <RocketOutlined /> },
      { label: 'Total Spent', value: '$8,518', change: 0.2, icon: <DollarOutlined /> },
      { label: 'CTR', value: '3.445%', change: null, icon: <PercentageOutlined /> },
      { label: 'CPC', value: '$0.83', change: -3.4, icon: <DollarOutlined /> },
      { label: 'Impressions', value: '337.0K', change: 15.8, icon: <EyeOutlined /> },
      { label: 'Impressions / Mile', value: '15', change: 7.2, icon: <LineChartOutlined /> }
    ]
  });

  return (
    <div className="enterprise-dashboard">
      {/* Header */}
      <div className="dashboard-header">
        <div>
          <h1 className="dashboard-title">Enterprise Administrator Dashboard</h1>
          <p className="dashboard-subtitle">Welcome back, Enterprise</p>
        </div>
        <div className="last-updated">Last updated: 6:32:50 PM</div>
      </div>

      {/* Quick Actions */}
      <div className="quick-actions-section">
        <h3 className="section-title">Quick Actions</h3>
        <Row gutter={16}>
          <Col span={8}>
            <Card className="quick-action-card">
              <div className="quick-action-icon">
                <EnvironmentOutlined style={{ color: '#3b82f6' }} />
              </div>
              <div className="quick-action-content">
                <h4>Location Management</h4>
                <p>Manage 9 active locations and their campaigns</p>
                <a href="/agency/locations">View Locations →</a>
              </div>
            </Card>
          </Col>
          <Col span={8}>
            <Card className="quick-action-card">
              <div className="quick-action-icon">
                <TeamOutlined style={{ color: '#8b5cf6' }} />
              </div>
              <div className="quick-action-content">
                <h4>Team Management</h4>
                <p>Manage team members and their access permissions</p>
                <a href="/agency/team">Manage Team →</a>
              </div>
            </Card>
          </Col>
          <Col span={8}>
            <Card className="quick-action-card">
              <div className="quick-action-icon">
                <CustomerServiceOutlined style={{ color: '#f59e0b' }} />
              </div>
              <div className="quick-action-content">
                <h4>Support</h4>
                <p>Get help with your account or contact our team</p>
                <a href="/agency/support">Get Support →</a>
              </div>
            </Card>
          </Col>
        </Row>
      </div>

      {/* Enterprise Overview */}
      <div className="enterprise-overview-section">
        <h2 className="section-title">Enterprise Overview</h2>
        <p className="section-subtitle">Monitor performance across all your locations and campaigns</p>
        
        <Card className="locations-overview-card">
          <div className="locations-overview-header">
            <div>
              <ShopOutlined style={{ fontSize: 24, marginRight: 12 }} />
              <span className="overview-title">Locations Overview</span>
            </div>
            <span className="overview-subtitle">All business locations at a glance</span>
          </div>
          <Row gutter={24} className="overview-stats">
            <Col span={4}>
              <div className="stat-item">
                <ShopOutlined className="stat-icon" />
                <div className="stat-value">{stats.locations}</div>
                <div className="stat-label">Total Locations</div>
              </div>
            </Col>
            <Col span={4}>
              <div className="stat-item">
                <CarOutlined className="stat-icon" />
                <div className="stat-value">{stats.vehicles}</div>
                <div className="stat-label">Total Vehicles</div>
              </div>
            </Col>
            <Col span={4}>
              <div className="stat-item">
                <RocketOutlined className="stat-icon" />
                <div className="stat-value">{stats.campaigns}</div>
                <div className="stat-label">Active Campaigns</div>
              </div>
            </Col>
            <Col span={4}>
              <div className="stat-item">
                <LineChartOutlined className="stat-icon" />
                <div className="stat-value">{stats.routeMiles}K</div>
                <div className="stat-label">Route Miles</div>
              </div>
            </Col>
            <Col span={4}>
              <div className="stat-item">
                <EyeOutlined className="stat-icon" />
                <div className="stat-value">{stats.impressionsPerMile}</div>
                <div className="stat-label">Impressions / Mile</div>
              </div>
            </Col>
            <Col span={4}>
              <div className="stat-item">
                <PercentageOutlined className="stat-icon" />
                <div className="stat-value">{stats.ctr}%</div>
                <div className="stat-label">Combined CTR</div>
              </div>
            </Col>
          </Row>
        </Card>
      </div>

      {/* Your Plan Details */}
      <div className="plan-details-section">
        <div className="section-header">
          <h3 className="section-title">Your Plan Details</h3>
          <a href="/agency/billing" className="manage-link">Manage Plan</a>
        </div>
        <Row gutter={16}>
          {/* RouteIQ */}
          <Col span={12}>
            <Card className="plan-card">
              <div className="plan-header">
                <div className="plan-icon routeiq-icon">
                  <CarOutlined />
                </div>
                <div>
                  <h4 className="plan-name">{planDetails.routeIQ.name}</h4>
                  <p className="plan-subtitle">{planDetails.routeIQ.subtitle}</p>
                </div>
              </div>
              <div className="plan-details">
                <div className="plan-item">
                  <div className="plan-item-label">Vehicles</div>
                  <div className="plan-item-value">
                    <strong>{planDetails.routeIQ.vehicles.count} units</strong>
                    <span className="tier-badge">{planDetails.routeIQ.vehicles.tier}</span>
                  </div>
                </div>
                <div className="plan-item">
                  <div className="plan-item-label">Locations</div>
                  <div className="plan-item-value">
                    <strong>{planDetails.routeIQ.locations.count} locations</strong>
                    <span className="tier-badge">{planDetails.routeIQ.locations.tier}</span>
                  </div>
                </div>
                <div className="plan-pricing">
                  {planDetails.routeIQ.pricing.map((item, idx) => (
                    <div key={idx} className="pricing-row">
                      <span>{item.label}</span>
                      <span>{item.value}</span>
                    </div>
                  ))}
                  <div className="pricing-row subtotal">
                    <span>Subtotal</span>
                    <strong>{planDetails.routeIQ.subtotal}</strong>
                  </div>
                </div>
              </div>
            </Card>
          </Col>

          {/* ProximityIQ */}
          <Col span={12}>
            <Card className="plan-card">
              <div className="plan-header">
                <div className="plan-icon proximityiq-icon">
                  <RocketOutlined />
                </div>
                <div>
                  <h4 className="plan-name">{planDetails.proximityIQ.name}</h4>
                  <p className="plan-subtitle">{planDetails.proximityIQ.subtitle}</p>
                </div>
              </div>
              <div className="plan-details">
                <div className="plan-item">
                  <div className="plan-item-label">SMS Credits</div>
                  <div className="plan-item-value">
                    <strong>{planDetails.proximityIQ.smsCredits.used.toLocaleString()} / {planDetails.proximityIQ.smsCredits.total.toLocaleString()}</strong>
                  </div>
                  <Progress 
                    percent={(planDetails.proximityIQ.smsCredits.used / planDetails.proximityIQ.smsCredits.total) * 100} 
                    showInfo={false}
                    strokeColor="#8b5cf6"
                  />
                </div>
                <div className="plan-item">
                  <div className="plan-item-label">Email Credits</div>
                  <div className="plan-item-value">
                    <strong>{planDetails.proximityIQ.emailCredits.used.toLocaleString()} / {planDetails.proximityIQ.emailCredits.total.toLocaleString()}</strong>
                  </div>
                  <Progress 
                    percent={(planDetails.proximityIQ.emailCredits.used / planDetails.proximityIQ.emailCredits.total) * 100} 
                    showInfo={false}
                    strokeColor="#3b82f6"
                  />
                </div>
                <div className="plan-pricing">
                  <div className="pricing-row">
                    <span>{planDetails.proximityIQ.basePlatform.label}</span>
                    <span>{planDetails.proximityIQ.basePlatform.value}</span>
                  </div>
                  <div className="pricing-row">
                    <span>{planDetails.proximityIQ.scalePackages.label}</span>
                    <span>{planDetails.proximityIQ.scalePackages.value}</span>
                  </div>
                  <div className="pricing-row subtotal">
                    <span>Monthly Total</span>
                    <strong>{planDetails.proximityIQ.monthlyTotal}</strong>
                  </div>
                </div>
              </div>
            </Card>
          </Col>
        </Row>

        {/* Billing Summary */}
        <Card className="billing-summary-card">
          <div className="billing-header">
            <div className="billing-icon">
              <DollarOutlined />
            </div>
            <div>
              <h4>Billing Summary</h4>
              <p>Current billing cycle</p>
            </div>
          </div>
          <div className="billing-details">
            <div className="billing-row">
              <span>RouteIQ</span>
              <span>$2,724</span>
            </div>
            <div className="billing-row">
              <span>ProximityIQ</span>
              <span>$2,151</span>
            </div>
            <div className="billing-row total">
              <span>Platform Total</span>
              <strong>{planDetails.billingTotal}</strong>
            </div>
            <p className="billing-note">~$500/vehicle ~$542/location (excl. ad spend)</p>
          </div>
        </Card>
      </div>

      {/* Analytics Overview */}
      <div className="analytics-overview-section">
        <h2 className="section-title">Analytics Overview</h2>

        {/* SMS Analytics */}
        <div className="analytics-category">
          <h3 className="category-title">SMS ANALYTICS</h3>
          <Row gutter={16}>
            {analytics.sms.map((item, idx) => (
              <Col span={4} key={idx}>
                <Card className="analytics-card">
                  <div className="analytics-icon">{item.icon}</div>
                  <div className="analytics-value">{item.value}</div>
                  <div className="analytics-label">{item.label}</div>
                  {item.change && (
                    <div className={`analytics-change ${item.change > 0 ? 'positive' : 'negative'}`}>
                      {item.change > 0 ? '↑' : '↓'} {Math.abs(item.change)}%
                    </div>
                  )}
                </Card>
              </Col>
            ))}
          </Row>
        </div>

        {/* Email Analytics */}
        <div className="analytics-category">
          <h3 className="category-title">EMAIL ANALYTICS</h3>
          <Row gutter={16}>
            {analytics.email.map((item, idx) => (
              <Col span={4} key={idx}>
                <Card className="analytics-card">
                  <div className="analytics-icon">{item.icon}</div>
                  <div className="analytics-value">{item.value}</div>
                  <div className="analytics-label">{item.label}</div>
                  {item.change && (
                    <div className={`analytics-change ${item.change > 0 ? 'positive' : 'negative'}`}>
                      {item.change > 0 ? '↑' : '↓'} {Math.abs(item.change)}%
                    </div>
                  )}
                </Card>
              </Col>
            ))}
          </Row>
        </div>

        {/* Meta Campaigns */}
        <div className="analytics-category">
          <h3 className="category-title">META CAMPAIGNS</h3>
          <Row gutter={16}>
            {analytics.meta.map((item, idx) => (
              <Col span={4} key={idx}>
                <Card className="analytics-card">
                  <div className="analytics-icon">{item.icon}</div>
                  <div className="analytics-value">{item.value}</div>
                  <div className="analytics-label">{item.label}</div>
                  {item.change && (
                    <div className={`analytics-change ${item.change > 0 ? 'positive' : 'negative'}`}>
                      {item.change > 0 ? '↑' : '↓'} {Math.abs(item.change)}%
                    </div>
                  )}
                </Card>
              </Col>
            ))}
          </Row>
        </div>

        {/* Google Analytics */}
        <div className="analytics-category">
          <h3 className="category-title">GOOGLE ANALYTICS</h3>
          <Row gutter={16}>
            {analytics.google.map((item, idx) => (
              <Col span={4} key={idx}>
                <Card className="analytics-card">
                  <div className="analytics-icon">{item.icon}</div>
                  <div className="analytics-value">{item.value}</div>
                  <div className="analytics-label">{item.label}</div>
                  {item.change && (
                    <div className={`analytics-change ${item.change > 0 ? 'positive' : 'negative'}`}>
                      {item.change > 0 ? '↑' : '↓'} {Math.abs(item.change)}%
                    </div>
                  )}
                </Card>
              </Col>
            ))}
          </Row>
        </div>
      </div>
    </div>
  );
}

export default Dashboard;
